import { Anchor, Box, Group, Loader, Paper, Stack, Text, UnstyledButton } from '@mantine/core'
import { IconAlertTriangle, IconCheck } from '@tabler/icons-react'
import dayjs from 'dayjs'
import { useAppTranslation } from 'hooks/useTranslation'
import type { CheckRow, ChecksPanelRow, DeskFieldId, RecheckLine } from './checkFieldMap'
import { checkLabelKey, checksInOrder, unknownChecks } from './checkFieldMap'
import { dateLocaleOf } from './compareRows'

/** The label key of each form section in "→ {field}" (UI-SPEC §Copy C). */
export const DESK_FIELD_LABEL_KEYS: Record<DeskFieldId, string> = {
  title: 'events.event_title',
  subtitle: 'events.subtitle',
  description: 'events.description',
  start: 'desk.fields.start',
  end: 'desk.fields.end',
  place: 'desk.fields.place',
  price: 'events.price',
  category: 'events.category',
  organizer: 'events.organizer',
  image: 'desk.fields.image'
}

/** The element ids the candidate screen gives its scroll targets. */
export const DESK_MATCH_PANEL_ID = 'desk-match-panel'
export const DESK_STATUS_BANNER_ID = 'desk-status-banner'
export const deskFieldElementId = (field: DeskFieldId) => `desk-field-${field}`

/**
 * Scroll an element into view and focus its first input (UI-SPEC §D). A target
 * that is not on screen (e.g. the match panel before 33-16 draws it) is a
 * no-op: the row stays listed, it just has nowhere to go.
 */
export const defaultScrollTo = (id: string) => {
  const element = document.getElementById(id)
  if (!element) return
  element.scrollIntoView?.({ behavior: 'smooth', block: 'center' })
  const input = element.querySelector<HTMLElement>('input, textarea, select')
  input?.focus()
}

const targetId = (row: ChecksPanelRow): string | null => {
  if (row.fields.length > 0) return deskFieldElementId(row.fields[0])
  if (row.target.kind === 'match_panel') return DESK_MATCH_PANEL_ID
  if (row.target.kind === 'status_banner') return DESK_STATUS_BANNER_ID
  return null
}

const targetLabelKeys = (row: ChecksPanelRow): string[] => {
  if (row.fields.length > 0) return row.fields.map((field) => DESK_FIELD_LABEL_KEYS[field])
  if (row.target.kind === 'match_panel') return ['desk.candidate.match_panel']
  if (row.target.kind === 'status_banner' || row.sourceKey === 'status') return ['desk.fields.status']
  return []
}

type Translate = (key: string, options?: Record<string, unknown>) => string

type CityName = { id: number; name: string }

export type ChecksPanelProps = {
  row: CheckRow
  onScrollTo?: (id: string) => void
  /** The duplicate re-check's line (33.1, checkFieldMap.recheckLine); null = today's output. */
  recheck?: RecheckLine | null
  /** "Check again": re-sends the last re-check (useDeskRecheck.retry). */
  onRecheck?: () => void
  /** Town names for the clear line. */
  cities?: ReadonlyArray<CityName>
}

/**
 * The clear line's text (D-13, D-26): the town checked and its day + short
 * month in the admin language. Town and date are the ones the re-check
 * stored on the row, so a reload says the same. No counts (owner ruling).
 */
const clearText = (
  line: Extract<RecheckLine, { kind: 'clear' }>,
  cities: ReadonlyArray<CityName>,
  language: string,
  t: Translate
): string => {
  const date = line.startDate ? dayjs(line.startDate).locale(dateLocaleOf(language)).format('D MMM') : ''
  const town = line.cityId === null ? undefined : cities.find((city) => city.id === line.cityId)?.name
  return town ? t('desk.recheck.clear', { town, date }) : t('desk.recheck.clear_no_town', { date })
}

type RecheckItemProps = {
  line: RecheckLine
  onRecheck?: () => void
  cities: ReadonlyArray<CityName>
  language: string
  t: Translate
}

/**
 * The re-check on the flag row (33.1 D-13, D-14): checking with a small
 * loader, clear with a green check, or failed with the "not checked" label
 * and, when another try can help, "Check again". The same markup wherever it
 * is drawn. "Check again" is not inside a scroll button and stops the click,
 * so it never scrolls the screen.
 */
const RecheckItem = ({ line, onRecheck, cities, language, t }: RecheckItemProps) => {
  if (line.kind === 'checking') {
    return (
      <Group gap={4} wrap='nowrap' align='center' data-recheck='checking'>
        <Loader size='xs' />
        <Text size='sm' c='dimmed'>
          {t('desk.recheck.checking')}
        </Text>
      </Group>
    )
  }
  if (line.kind === 'clear') {
    return (
      <Group gap={4} wrap='nowrap' align='center' data-recheck='clear'>
        <IconCheck size={16} color='var(--mantine-color-green-6)' style={{ flexShrink: 0 }} />
        <Text size='sm'>{clearText(line, cities, language, t)}</Text>
      </Group>
    )
  }
  return (
    <Group gap={8} wrap='nowrap' align='center' data-recheck='failed'>
      <Group gap={4} wrap='nowrap' align='center'>
        <IconAlertTriangle size={16} color='var(--mantine-color-orange-6)' style={{ flexShrink: 0 }} />
        <Text size='sm'>{t(line.labelKey)}</Text>
      </Group>
      {line.retryable && (
        <Anchor
          component='button'
          type='button'
          size='sm'
          onClick={(event) => {
            event.stopPropagation()
            onRecheck?.()
          }}
        >
          {t('desk.recheck.check_again')}
        </Anchor>
      )}
    </Group>
  )
}

/**
 * "What the editor flagged" (DESK-02, D-07, UI-SPEC §D). Every route check in
 * the closed D-07 order, one row per conflicting field, and any check name this
 * admin does not know listed as is (never hidden, T-33-38). With nothing to
 * list the panel is not drawn: a Ready candidate never carries a check
 * (applications_route_shape), so it would only repeat what the tab says.
 */
export const ChecksPanel = ({
  row,
  onScrollTo = defaultScrollTo,
  recheck = null,
  onRecheck,
  cities = []
}: ChecksPanelProps) => {
  const { t, language } = useAppTranslation()
  const rows = checksInOrder(row)
  const unknown = unknownChecks(row)
  if (rows.length === 0 && unknown.length === 0 && !recheck) return null

  const recheckItem = recheck && (
    <RecheckItem line={recheck} onRecheck={onRecheck} cities={cities} language={language} t={t} />
  )
  // A desk_rules row with a result carries no match_unchecked (D-18): the
  // re-check line then goes first, so a second town change still shows it.
  const hasSlot = rows.some((checkRow) => checkRow.check === 'match_unchecked')

  return (
    <Paper withBorder p={16}>
      <Stack gap={8}>
        <Text size='md' fw={600} lh={1.3}>
          {t('desk.candidate.flagged_title')}
        </Text>
        {recheckItem && !hasSlot && <Box data-recheck-slot='first'>{recheckItem}</Box>}
        {rows.map((checkRow) => {
          if (recheckItem && checkRow.check === 'match_unchecked') {
            return (
              <Box key='match_unchecked-recheck' data-check='match_unchecked'>
                {recheckItem}
              </Box>
            )
          }
          const id = targetId(checkRow)
          const labels = targetLabelKeys(checkRow).map((key) => t(key))
          return (
            <UnstyledButton
              key={`${checkRow.check}-${checkRow.sourceKey ?? ''}`}
              data-check={checkRow.check}
              onClick={() => {
                if (id) onScrollTo(id)
              }}
            >
              <Group gap={4} wrap='nowrap' align='center'>
                <IconAlertTriangle size={16} color='var(--mantine-color-orange-6)' style={{ flexShrink: 0 }} />
                <Text size='sm'>{t(checkLabelKey(checkRow))}</Text>
                {labels.length > 0 && (
                  <Text size='xs' lh={1.4} c='orange.8'>
                    {t('desk.candidate.check_target', { field: labels.join(', ') })}
                  </Text>
                )}
              </Group>
            </UnstyledButton>
          )
        })}
        {unknown.map((name) => (
          <Group key={`unknown-${name}`} gap={4} wrap='nowrap' align='center'>
            <IconAlertTriangle size={16} color='var(--mantine-color-orange-6)' style={{ flexShrink: 0 }} />
            <Text size='sm'>{name}</Text>
          </Group>
        ))}
      </Stack>
    </Paper>
  )
}
